import React, { useState } from 'react';
import styled from 'styled-components';
import { Mail, Phone, Linkedin, MapPin, Send } from 'lucide-react';
import { API_CONFIG } from '../config/api';

const ContactSection = styled.section`
  padding: 5rem 0;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 0 1rem;
  }
`;

const ContactHeader = styled.div`
  text-align: center;
  margin-bottom: 4rem;

  h2 {
    font-size: 2.5rem;
    margin-bottom: 1rem;
  }

  p {
    font-size: 1.2rem;
    opacity: 0.9;
    max-width: 600px;
    margin: 0 auto;
  }
`;

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const ContactInfo = styled.div`
  h3 {
    font-size: 1.8rem;
    margin-bottom: 2rem;
  }
`;

const ContactItem = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  backdrop-filter: blur(10px);

  .icon {
    font-size: 1.5rem;
    color: #ff6900;
  }

  .content {
    flex: 1;

    .label {
      font-size: 0.9rem;
      opacity: 0.8;
      margin-bottom: 0.2rem;
    }

    .value {
      font-size: 1.1rem;
      font-weight: 500;
    }
  }
`;

const ContactForm = styled.form`
  background: rgba(255, 255, 255, 0.1);
  padding: 2rem;
  border-radius: 15px;
  backdrop-filter: blur(10px);

  h3 {
    font-size: 1.8rem;
    margin-bottom: 2rem;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;

  label {
    display: block;
    margin-bottom: 0.5rem;
    font-weight: 500;
  }

  input,
  textarea {
    width: 100%;
    padding: 1rem;
    border: none;
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.9);
    color: #333;
    font-size: 1rem;
    box-sizing: border-box;

    &:focus {
      outline: none;
      box-shadow: 0 0 0 2px #ff6900;
    }

    &::placeholder {
      color: #666;
    }
  }

  textarea {
    resize: vertical;
    min-height: 120px;
  }
`;

const SubmitButton = styled.button`
  background-color: #ff6900;
  color: white;
  border: none;
  padding: 1rem 2rem;
  font-size: 1.1rem;
  font-weight: 600;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.3s ease;
  width: 100%;
  justify-content: center;

  &:hover {
    background-color: #e55a00;
    transform: translateY(-2px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const Footer = styled.footer`
  background-color: #2c3e50;
  color: white;
  text-align: center;
  padding: 2rem;
  margin-top: 0;

  p {
    margin: 0;
    opacity: 0.8;
  }
`;

const Contact: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await fetch(API_CONFIG.CONTACT_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      
      if (response.ok) {
        await response.json();
        setFormData({ name: '', email: '', company: '', message: '' });
        alert('Thank you for your message! We\'ll get back to you soon.');
      } else {
        const error = await response.json();
        alert(`Error: ${error.error || 'Failed to send message. Please try again.'}`);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('Failed to send message. Please check your connection and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <ContactSection id="contact">
        <Container>
          <ContactHeader>
            <h2>Let's Discuss Your Data Engineering Needs</h2>
            <p>
              Ready to transform your data infrastructure? Get in touch for a free consultation 
              on your data engineering challenges and opportunities.
            </p>
          </ContactHeader>

          <ContactGrid>
            <ContactInfo>
              <h3>Get In Touch</h3>
              
              <ContactItem>
                <Mail className="icon" />
                <div className="content">
                  <div className="label">Email</div>
                  <div className="value">hello@rjspark.com</div>
                </div>
              </ContactItem>

              <ContactItem>
                <Phone className="icon" />
                <div className="content">
                  <div className="label">Phone</div>
                  <div className="value">+1 (555) 123-4567</div>
                </div>
              </ContactItem>

              <ContactItem>
                <Linkedin className="icon" />
                <div className="content">
                  <div className="label">LinkedIn</div>
                  <div className="value">linkedin.com/in/rjspark</div>
                </div>
              </ContactItem>

              <ContactItem>
                <MapPin className="icon" />
                <div className="content">
                  <div className="label">Location</div>
                  <div className="value">Remote & On-site Consulting</div>
                </div>
              </ContactItem>
            </ContactInfo>

            <ContactForm onSubmit={handleSubmit}>
              <h3>Send us a Message</h3>
              
              <FormGroup>
                <label htmlFor="name">Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Your full name"
                  required
                />
              </FormGroup>

              <FormGroup>
                <label htmlFor="email">Email *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="your.email@company.com"
                  required
                />
              </FormGroup>

              <FormGroup>
                <label htmlFor="company">Company</label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleInputChange}
                  placeholder="Your company name"
                />
              </FormGroup>

              <FormGroup>
                <label htmlFor="message">Message *</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  placeholder="Tell us about your data engineering needs, current challenges, or project requirements..."
                  required
                />
              </FormGroup>

              <SubmitButton type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Sending...' : 'Send Message'} <Send />
              </SubmitButton>
            </ContactForm>
          </ContactGrid>
        </Container>
      </ContactSection>

      <Footer>
        <p>&copy; 2024 RJSpark. All rights reserved. | Specializing in Healthcare Data Engineering</p>
      </Footer>
    </>
  );
};

export default Contact;